import * as React from "react";
import { graphql } from "gatsby";

import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import PageContainer from "../pageContainer";
import { when } from "../../styles/theme";
import { useInView } from "react-intersection-observer";
import { SectionTitle } from "../typography.js";
import theme from "../../styles/theme.js";
import pageBackground from "../../images/page_background_blue.jpg";

const Wrapper = styled.section`
  ${(props) =>
    props.isDark &&
    `background: ${props.theme.colors.darkBlue}; color: ${props.theme.colors.white};`}
  position: relative;
  pointer-events: none;
`;

const Container = styled(PageContainer)`
  padding-top: 10rem;
  ${(props) => props.isDark && `padding-bottom: 10rem;`}
  position: relative;
  z-index: 10;
`;

const ContentSection = styled.div`
  margin-top: 5rem;
  pointer-events: auto;

  &:first-child {
    margin-top: 0;
  }

  ${when("md")} {
    display: flex;
    align-items: ${(props) => (props.alignCenter ? "center" : "flex-start")};
    flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};
    column-gap: ${(props) => props.theme.gutterWidth.md}rem;
    margin-top: ${(props) => (props.reverse ? "10rem" : "5rem")};
  }

  ${when("lg")} {
    column-gap: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    column-gap: ${(props) => props.theme.gutterWidth.xl}rem;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.reverse ? "flex-end" : "flex-start")};

  ${when("md")} {
    flex: 1;
    align-self: flex-start;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 100rem;
  width: 25rem;
  margin-right: 1.5rem;
  margin-bottom: 1rem;
  max-width: 45rem;

  ${when("md")} {
    width: 80%;
  }
`;

const Circle = styled.div`
  position: absolute;
  width: 80%;
  height: 80%;
  bottom: -1rem;
  right: -1.5rem;
  background-color: ${(props) =>
    props.isCircleRed ? props.theme.colors.red : props.theme.colors.darkBlue};
  border-radius: 9999rem;
`;

const Image = styled(GatsbyImage)`
  border-radius: 50%;
  overflow: hidden;
  transform: translateZ(0);
`;

const Content = styled.div`
  ${when("md")} {
    flex: 1;
  }
`;

const Title = styled(SectionTitle)`
  margin-top: 4rem;
  line-height: 1;

  ${when("md")} {
    margin-top: 0;
    /* font-size: ${(props) => (props.isCompact ? 4.8 : 6.4)}rem; */
  }
`;

const DescriptionContainer = styled.div`
  color: ${(props) =>
    props.isDark ? props.theme.colors.white : props.theme.colors.darkBlue};
  margin-top: 3rem;
  max-width: 60rem;
  margin-left: ${(props) => (props.reverse ? "auto" : "0")};

  ${when("md")} {
    margin-top: ${(props) => (props.reverse ? "0" : "3")}rem;
  }
`;

export const fragment = graphql`
  fragment TextIntroductionContent on ContentfulPageSectionTextIntroduction {
    name
    title
    paragraph1 {
      childMarkdownRemark {
        html
      }
    }
    paragraphImage1 {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
    }
    paragraph2 {
      childMarkdownRemark {
        html
      }
    }
    paragraphImage2 {
      description
      gatsbyImageData(layout: CONSTRAINED, width: 450, height: 450)
    }
    isCompact
    isCircleRed

    internal {
      type
    }
  }
`;
const ContainerBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  //z-index: -1;
  background-image: url(${pageBackground});
  background-repeat: repeat;
  background-position: center;
`;

const TextIntroduction = ({ data, setCurrentSection, index }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const {
    title,
    paragraph1,
    paragraphImage1,
    paragraph2,
    paragraphImage2,
    isCompact,
    isCircleRed,
  } = data;

  return (
    <Wrapper isDark={!isCompact}>
      {!isCompact && <ContainerBackground />}
      <Container
        hasSideBar={isCompact}
        ref={ref}
        id={`section-${index}`}
        isDark={!isCompact}
      >
        <ContentSection>
          {paragraphImage1 && (
            <ImageContainer>
              <ImageWrapper>
                <Circle isCircleRed={isCircleRed} />
                <Image
                  image={paragraphImage1.gatsbyImageData}
                  alt={paragraphImage1.description}
                />
              </ImageWrapper>
            </ImageContainer>
          )}
          <Content>
            <Title
              isCompact={isCompact}
              color={!isCompact ? theme.colors.white : null}
            >
              {title}
            </Title>
            <DescriptionContainer
              isDark={!isCompact}
              dangerouslySetInnerHTML={{
                __html: paragraph1?.childMarkdownRemark.html,
              }}
            />
          </Content>
        </ContentSection>
        <ContentSection reverse={true} alignCenter={true}>
          {paragraphImage2 && (
            <ImageContainer reverse={true}>
              <ImageWrapper>
                <Circle isCircleRed={isCircleRed} />
                <Image
                  image={paragraphImage2.gatsbyImageData}
                  alt={paragraphImage2.description}
                />
              </ImageWrapper>
            </ImageContainer>
          )}
          <Content>
            <DescriptionContainer
              isDark={!isCompact}
              reverse={true}
              dangerouslySetInnerHTML={{
                __html: paragraph2?.childMarkdownRemark.html,
              }}
            />
          </Content>
        </ContentSection>
      </Container>
    </Wrapper>
  );
};

export const textIntroductionName = "ContentfulPageSectionTextIntroduction";

export default TextIntroduction;
